// L5 — **THE EVIDENCE FILE: MEMBERS WHO KEPT PUSHING PAST A WARM NO**, as its own component.
//
// Extracted by ITEM 4b (2026-08-13) to pay App.tsx's R8 ceiling as that round went, and it is
// `r8-leaves.mjs`'s favourite currency once more: *the thing that had to move is the thing nothing
// could reach.* Sixty lines of JSX that decide what a member's peak strike is CALLED, which sentence
// an empty list gets, and whether her name is a door — none of it reachable by any instrument while
// it sat inside a twelve-thousand-line file.
//
// **HOOKLESS, WHICH IS THE ENTRY REQUIREMENT (R7):** the list, the filter, the open row, the loaded
// evidence and every act arrive as props, so a gate CALLS it and reads the tree back. The state
// stays with the dashboard that owns every other one.
//
// **MOVED VERBATIM.** No member-visible line changed in the extraction, so the round's render risk is
// carried by the build, the typecheck and the gate rather than by a claim.
import { MemberDoor } from "./MemberDoor";
import { OwnerEvidenceDrawer, type EvidenceEvent, type EvidenceRuling } from "./OwnerEvidence";
// HER DASHBOARD ITEM, SURFACE 1: what an empty screen SAYS is a law a gate RUNS, not a ternary here.
import { probeEmptyState } from "../probeClears";

export type ProbeRow = {
  userId: string; email: string; name: string; events: number; spice: number;
  termsWarnings: number; cooldowns: number; blockedAttempts: number;
  maxStrike: number | null; topics: string[]; recentAsks: string[]; lastAt: string;
  clearedAt?: string;
};

/** What a row's heading says. The peak strike rides on the end and only when there IS one (T9-2). */
export function probeHeading(p: ProbeRow): string {
  const lead = p.cooldowns > 0 ? `${p.cooldowns} cooldown${p.cooldowns === 1 ? "" : "s"}`
    : p.termsWarnings > 0 ? "Terms warned"
    : p.spice > 0 ? "Checked with spice"
    : "Strikes";
  return `${lead}${p.maxStrike ? ` · peak strike ${p.maxStrike}` : ""}`;
}

export function OwnerProbes({
  probes, filter, loading, openEntry, evidence, evidenceLoading, evidenceNote, rulingNote,
  view = "new", clearNote = "",
  shortDate, onFilter, onOpenMember, onToggle, onRule, onView, onClear,
}: {
  probes: ProbeRow[];
  filter: string;
  loading: boolean;
  openEntry: string;
  evidence: Record<string, EvidenceEvent[]>;
  evidenceLoading: string;
  evidenceNote: Record<string, string>;
  rulingNote: Record<string, string>;
  view?: string;
  clearNote?: string;
  shortDate: (iso: string) => string;
  onFilter: (next: string) => void;
  onOpenMember: (id: string) => void;
  onToggle: (userId: string) => void;
  onRule: (userId: string, id: string, ruling: EvidenceRuling) => void;
  onView?: (next: string) => void;
  onClear?: (userId: string | null) => void;
}) {
  const archive = view === "archive";
  return (
    <>
      <div className="owner-bar">
        <p className="owner-page__note">
          {archive
            ? "Everything you have cleared, kept in full. Nothing here comes back to the working screen."
            : "Members who kept pushing past a warm no on your machinery, your family, or your money. This is the evidence file for deciding who to remove."}
        </p>
        <input className="owner-search" value={filter} onChange={(e) => onFilter(e.target.value)}
          placeholder="Search probes..."
          aria-label={archive ? "Search the archive" : "Search probes"} />
      </div>
      {/* HER CLAUSE: *a clear control per item, and a clear-all* — plus the small Archive link that
          opens history with search. The clear-all is offered only where there is something to clear,
          so the working screen never draws a control over an empty list (T9-2's no-body-no-box law
          one surface over). */}
      <div className="owner-bar owner-bar--probes">
        <button type="button" className="owner-link" onClick={() => onView?.(archive ? "new" : "archive")}>
          {archive ? "Back to new" : "Archive"}
        </button>
        {!archive && probes.length ? (
          <button type="button" className="owner-link" onClick={() => onClear?.(null)}>Clear all</button>
        ) : null}
      </div>
      {clearNote ? <p className="owner-page__note">{clearNote}</p> : null}
      {loading ? <p className="owner-page__note">Loading...</p> : null}
      <div className="owner-messages">
        {probes.map((p) => (
          <article className="owner-message" key={p.userId}>
            <header>
              {/* D6-4 — the member's name is a door here too. It was the one place on this
                  dashboard that named a member and gave her nowhere to go with it. */}
              <span className="owner-message__reason">
                <MemberDoor id={p.userId} name={p.name} email={p.email} onOpen={onOpenMember} />
              </span>
              <span className="owner-message__date">{shortDate(p.lastAt)}</span>
            </header>
            <h3>{probeHeading(p)}</h3>
            <p className="owner-page__note">
              {p.email ? `${p.email} · ` : ""}topics: {p.topics.join(", ") || "protected"} · {p.events} event{p.events === 1 ? "" : "s"}
              {p.blockedAttempts > 0 ? ` · ${p.blockedAttempts} asked during a lock` : ""}
            </p>
            <button type="button" className="owner-link" onClick={() => onToggle(p.userId)}>
              {openEntry === p.userId ? "Hide the evidence" : "Review the evidence"}
            </button>
            {/* Her clear control, per item. It is offered on the working screen only: the archive is
                where cleared work already lives, and a second clear there would be a control that
                cannot change anything. What it says is what it does — the row leaves this screen and
                stays in the Archive, and it is never called *delete*, because nothing is deleted. */}
            {!archive ? (
              <button type="button" className="owner-link" onClick={() => onClear?.(p.userId)}>Clear</button>
            ) : (
              <span className="owner-page__note">Cleared {shortDate(p.clearedAt || "")}</span>
            )}
            {openEntry === p.userId ? (
              <OwnerEvidenceDrawer
                events={evidence[p.userId]}
                loading={evidenceLoading === p.userId}
                loadNote={evidenceNote[p.userId] || ""}
                actNote={rulingNote[p.userId] || ""}
                onRule={(id, ruling) => onRule(p.userId, id, ruling)}
                shortDate={shortDate}
              />
            ) : null}
          </article>
        ))}
        {!loading && !probes.length ? (
          <p className="owner-page__note">
            {/* FOUR FACTS, NEVER ONE (R18/R64) — `probeEmptyState` decides, so an empty archive can
                never be reported as a quiet week, and a search that matched nobody can never be
                reported as an empty archive. */}
            {probeEmptyState({ loading, needle: filter, view }) === "no-match"
              ? "No one matches that."
              : probeEmptyState({ loading, needle: filter, view }) === "archive-empty"
                ? "You have not cleared anything yet. What you clear is kept here in full."
                : "Nothing new. This fills in only when a member keeps probing after you have declined."}
          </p>
        ) : null}
      </div>
    </>
  );
}
